import useSWR, { mutate as globalMutate } from 'swr';
import { getMyScheduledItems } from '../services/myScheduleService';
import { getLocalISODate } from '../utils/dateUtils';

/**
 * Cache key for My Scheduled items.
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @param {string[]|null} types
 */
export function getMyScheduledItemsCacheKey(startDate, endDate, types = null, jobFilterIds = null, leadFilterIds = null, viewMyJobs = false, viewTraderTasks = null, advancedFilters = {}) {
  if (advancedFilters?.skipFetch) return null;
  
  const serializedTypes = Array.isArray(types) ? [...types].sort().join(',') : (types || null);
  const serializedJobIds = Array.isArray(jobFilterIds) ? [...jobFilterIds].sort().join(',') : (jobFilterIds || null);
  const serializedLeadIds = Array.isArray(leadFilterIds) ? [...leadFilterIds].sort().join(',') : (leadFilterIds || null);
  const serializedFilters = JSON.stringify(advancedFilters);
  return ['mySchedule', 'items', startDate, endDate, serializedTypes, serializedJobIds, serializedLeadIds, viewMyJobs, viewTraderTasks, serializedFilters];
}


/** Invalidate all My Schedule items cache entries. */
export function invalidateMyScheduledItemsCache() {
  return globalMutate(
    (key) => Array.isArray(key) && key[0] === 'mySchedule' && key[1] === 'items',
    undefined,
    { revalidate: true }
  );
}

/**
 * Hook to fetch My Scheduled items.
 * 
 * @param {string|Date|null} startDate
 * @param {string|Date|null} endDate
 * @param {string[]|null} types
 * @param {string[]|null} jobFilterIds
 * @param {string[]|null} leadFilterIds
 * @param {boolean} viewMyJobs
 * @param {boolean|null} viewTraderTasks
 */
export function useMyScheduledItems(startDate, endDate, types = null, jobFilterIds = null, leadFilterIds = null, viewMyJobs = false, viewTraderTasks = null, advancedFilters = {}) {
  const startStr = startDate
    ? (startDate instanceof Date ? getLocalISODate(startDate) : startDate)
    : null;
  const endStr = endDate
    ? (endDate instanceof Date ? getLocalISODate(endDate) : endDate)
    : null;

  const key = getMyScheduledItemsCacheKey(startStr, endStr, types, jobFilterIds, leadFilterIds, viewMyJobs, viewTraderTasks, advancedFilters);

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      const result = await getMyScheduledItems(startStr, endStr, types, jobFilterIds, leadFilterIds, viewMyJobs, viewTraderTasks, advancedFilters);

      if (result?.success) {
        return result.data || { jobs: [], leadActivities: [], jobActivities: [] };
      }

      throw new Error(result?.error || result?.message || 'Failed to fetch scheduled items');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      errorRetryCount: 2,
      errorRetryInterval: 5000,
      refreshWhenOffline: false,
      suspense: false,
    }
  );

  const refresh = () => invalidateMyScheduledItemsCache();

  return {
    data: data || { jobs: [], leadActivities: [], jobActivities: [] },
    isLoading,
    error,
    mutate,
    refresh,
  };
}
